import React, { useState, useRef } from "react";
import { FileText as FileTextIcon } from "lucide-react";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import { useEtatGlobal } from "../EtatGlobal";
import FloatingPanel from "../Home/FloatingPanel.js";
import Rdv from "../Rdv/Rdv.js";

import { authFetch } from '../../HttpClient';
import { uploadFileToBlob } from "../../utils/uploadFileToBlob";
import { showStatusToast } from "../../components/ui/Toast";

import { dialog } from "../../components/ui/Dialog";
import LeadDocumentsList from "../../components/LeadDocumentsList";
import {
  X,
  Building2,
  MoreVertical,
  Users,
  User,
  Calendar,
  FileText,
  Briefcase,
  MapPin,
  AlertCircle,
  Loader2,
  Check,
} from "lucide-react";

function RdvLM({ leadData, loading, onUpdate, searchTerm, columnId = "rdv_passe", loadingLeadIds, onOptimisticUpdate, onOptimisticClear }) {
  const [showRdvPanel, setShowRdvPanel] = React.useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState({});
  const overlayRef = useRef(null);
  const inoverlayRef = useRef(null);
  const debounceRef = useRef(null);
  const pendingValuesRef = useRef({});

  const [showMenu, setShowMenu] = useState(false);
  const [activeTab, setActiveTab] = useState("general");

  const [openDetails, setOpenDetails] = React.useState({});

  const { setAfficheLM, setFormData, setLeadIdGlobal, setLeads } =
    useEtatGlobal();

  const handleAfficheLM = (selectedLead) => {
    setFormData((prev) => ({
      ...prev,
      nom: selectedLead.nom || " ",
      prenom: selectedLead.prenom || " ",
      civilite_lm: selectedLead.civilite === "M." ? "Monsieur" : "Madame",
      email: selectedLead.mail || " ",
      telephone: selectedLead.tel || " ",
      adresse: selectedLead.adresse || " ",
      siren: selectedLead.sirenSociete || " ",
      siret: selectedLead.sirenSociete || " ",
      nom_client: selectedLead.societeNom || " ",
      forme_sociale: selectedLead.formeSociale || "EI",
      objetRDV: selectedLead.objetRDV || " ",
    }));

    setLeadIdGlobal(selectedLead.id);

    setAfficheLM(true);
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setFormValues({ ...lead });
    pendingValuesRef.current = { ...lead };
    setIsEditing(false);
    setShowOverlay(true);
  };

  const saveInBackground = async (leadId, values) => {
    // Upload direct navigateur → Blob pour les pièces jointes (contourne la
    // limite 4,5 Mo) : un toast informe qu'un envoi de fichier — parfois
    // long selon sa taille — est en cours, sans bloquer l'overlay déjà fermé.
    let toast = null;
    try {
      const fileEntries = Object.entries(values).filter(([, val]) => val instanceof File);
      const payload = { ...values };
      fileEntries.forEach(([key]) => delete payload[key]);

      if (fileEntries.length > 0) {
        toast = showStatusToast({ status: "loading", titre: "Envoi du document...", message: "0 %" });
        for (const [key, file] of fileEntries) {
          const url = await uploadFileToBlob(
            file,
            "https://backend-myalfa.vercel.app/api/formleads/upload-token",
            ({ percentage }) =>
              toast.update({ status: "loading", titre: "Envoi du document...", message: `${Math.round(percentage)} %` })
          );
          payload[`${key}Url`] = url;
        }
      }

      const res = await authFetch(`https://backend-myalfa.vercel.app/api/formleads/${leadId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Erreur");
      if (toast) toast.update({ status: "success", titre: "Document envoyé", message: "" });
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("Sauvegarde en arrière-plan échouée :", err);
      if (toast) toast.update({ status: "error", titre: "Échec de l'envoi", message: err.message });
    } finally {
      onOptimisticClear?.(leadId);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const leadId = selectedLead.id;
    setFormValues((prev) => {
      const next = { ...prev, [name]: value };
      pendingValuesRef.current = next;
      return next;
    });
    setSelectedLead((prev) => ({ ...prev, [name]: value }));
    onOptimisticUpdate?.(leadId, { [name]: value });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      saveInBackground(leadId, pendingValuesRef.current);
    }, 600);
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const leadId = selectedLead.id;
      setFormValues((prev) => {
        const next = { ...prev, [name]: files[0] };
        pendingValuesRef.current = next;
        return next;
      });
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        saveInBackground(leadId, pendingValuesRef.current);
      }, 600);
    }
  };

  const handleSave = () => {
    const leadId = selectedLead.id;
    const values = { ...formValues };

    // UI instantanée : la carte et le détail affichent déjà les nouvelles valeurs
    setSelectedLead((prev) => ({ ...prev, ...values }));
    setIsEditing(false);
    setShowOverlay(false);
    onOptimisticUpdate?.(leadId, values);

    // Sauvegarde réelle en arrière-plan
    if (debounceRef.current) clearTimeout(debounceRef.current);
    saveInBackground(leadId, values);
  };

  const handleArchive = async () => {
    const commentaire = window.prompt(
      "Voulez-vous vraiment archiver ce lead ?\n\nVous pouvez saisir un commentaire ci-dessous :"
    );

    if (commentaire === null) return; // L'utilisateur a annulé

    const leadToArchive = selectedLead;
    const payload = {
      specificite: "archive",
      lastspecificite: leadToArchive.specificite,
      commentaire, // Inclure le texte entré ici
    };

    // UI instantanée : la carte part aux archives tout de suite,
    // l'appel API se fait en arrière-plan
    setShowOverlay(false);
    setSelectedLead(null);
    setLeads((prev) =>
      prev.map((l) => (l.id === leadToArchive.id ? { ...l, ...payload } : l))
    );

    try {
      const res = await authFetch(`https://backend-myalfa.vercel.app/api/formleads/${leadToArchive.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Échec de l'archivage");
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error(err);
      // Échec : la carte revient dans sa colonne d'origine
      setLeads((prev) =>
        prev.map((l) => (l.id === leadToArchive.id ? leadToArchive : l))
      );
      dialog.alert("Impossible d'archiver ce lead.");
    }
  };

  const handleDelete = async () => {
    const confirmDelete = await dialog.confirm(
      "Voulez-vous vraiment supprimer ce lead ? Cette action est irréversible."
    );
    if (!confirmDelete) return;

    // UI instantanée : la carte disparaît tout de suite,
    // la suppression réelle se fait en arrière-plan
    const leadToDelete = selectedLead;
    setShowOverlay(false);
    setSelectedLead(null);
    setLeads((prev) => prev.filter((l) => l.id !== leadToDelete.id));

    try {
      const res = await authFetch(`https://backend-myalfa.vercel.app/api/formleads/${leadToDelete.id}`,
        {
          method: "DELETE",
        }
      );

      if (!res.ok) throw new Error("Échec de la suppression");

      if (onUpdate) onUpdate(); // Pour resynchroniser la liste
    } catch (err) {
      console.error(err);
      // Échec : on remet la carte
      setLeads((prev) => [...prev, leadToDelete]);
      dialog.alert("Impossible de supprimer ce lead.");
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0); // On prend seulement la date d'aujourd'hui à 00:00

  const filteredLeads = leadData
    .filter((lead) => lead.specificite === "rdv" && lead.status !== "LM")
    .filter((lead) =>
      `${lead.nom} ${lead.prenom}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
    .filter((lead) => {
      const leadDate = new Date(lead.dateRDV);
      leadDate.setHours(0, 0, 0, 0); // On ignore l'heure pour comparer seulement la date
      return leadDate < today; // Seulement les RDV avant aujourd'hui
    })
    // Date décroissante : le RDV le plus récemment passé en premier.
    .sort((a, b) => new Date(b.dateRDV) - new Date(a.dateRDV));

  const noteRDVsFormatted = (selectedLead?.noteRDVs || []).map((note) => ({
    id: `note-${note.id}`,
    titre: note.titre || "Note de rendez-vous",
    createdAt: note.createdAt,
    type: "noteRDV",
    noteDeBase: note.noteDeBase,
    noteResume: note.noteResume,
    documentUrl: note.documentUrl,
    documentName: note.documentName,
  }));

  const allTimeline = [
    ...(selectedLead?.HistoriqueFormLEAD || []),
    ...noteRDVsFormatted,
  ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <div>
      <div className="relative flex justify-center mb-5">
        <h2 className="relative inline-flex w-[88%] items-center justify-center rounded-2xl border border-[#6e1a36] bg-[#f3edef] px-6 py-2 text-sm font-semibold tracking-wide text-pink-800 shadow-md transition-all duration-300 hover:shadow-lg hover:scale-[1.02]">
          RDV terminé
          {/* Badge compteur */}
          <span className="absolute -top-2 -right-3 flex h-6 min-w-[24px] items-center justify-center rounded-full bg-[#6e1a36] px-2 text-[11px] font-bold text-white shadow-md">
            {filteredLeads.length > 0 && `${filteredLeads.length}`}
          </span>
        </h2>
      </div>

      <Droppable droppableId={columnId} isDropDisabled={loading}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`min-h-12 rounded-xl transition-all duration-200 ${
              snapshot.isDraggingOver
                ? "bg-[#9B1B5D]/5 ring-2 ring-[#9B1B5D]/20 ring-inset"
                : ""
            }`}
          >
            {loading ? (
              <div>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="mx-[4%] mb-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-md">
                    <div className="h-3 rounded bg-slate-200 animate-pulse w-36 mb-1.5" />
                    <div className="h-2.5 rounded bg-slate-200 animate-pulse w-24 mb-2" />
                    <div className="h-2.5 rounded bg-slate-200 animate-pulse w-40 mb-2" />
                    <div className="h-2.5 rounded bg-slate-200 animate-pulse w-44" />
                  </div>
                ))}
              </div>
            ) : leadData.length === 0 ? (
              <p style={{ textAlign: "center" }}>Aucun lead trouvé.</p>
            ) : (
              filteredLeads.map((lead, index) => (
                <Draggable key={String(lead.id)} draggableId={String(lead.id)} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      onClick={() => handleLeadClick(lead)}
                      className={`relative mx-[4%] mb-3 cursor-pointer rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 shadow-md transition-all duration-300 hover:shadow-xl hover:border-[#840040] hover:from-white hover:to-[#840040/5] ${
                        snapshot.isDragging
                          ? "shadow-2xl border-[#840040] scale-[1.02] opacity-90 rotate-1"
                          : "hover:-translate-y-1"
                      }`}
                    >
                      <p className="text-xs font-bold text-gray-800 leading-tight w-full">
                        {`${lead.civilite} ${lead.prenom} ${lead.nom}`.length > 21
                          ? `${lead.civilite} ${lead.prenom} ${lead.nom}`.slice(0, 21) + "..."
                          : `${lead.civilite} ${lead.prenom} ${lead.nom}`}
                      </p>
                      <span className="text-[11px] text-gray-400 font-medium tracking-wide">
                        {lead.societeNom}
                      </span>
                      <p className="mt-1 text-[11px] text-gray-500 font-normal">
                        {lead.objetRDV?.length > 25
                          ? lead.objetRDV.slice(0, 25) + "..."
                          : lead.objetRDV}
                      </p>
                      <div className="mt-1 flex justify-start">
                        <p className="text-[10px] text-gray-500 font-normal">
                          RDV fait le :{" "}
                          {new Date(lead.dateRDV).toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}{" "}
                          à {lead.heureRDV}
                        </p>
                      </div>
                      {loadingLeadIds?.has(lead.id) && (
                        <div className="absolute inset-0 bg-white/80 rounded-2xl flex items-center justify-center z-10">
                          <Loader2 className="w-4 h-4 text-[#9B1B5D] animate-spin" />
                        </div>
                      )}
                    </div>
                  )}
                </Draggable>
              ))
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>

      {showRdvPanel && selectedLead && (
        <FloatingPanel
          title="Rendez-vous"
          onClose={() => setShowRdvPanel(false)}
        >
          <Rdv
            formLeadId={selectedLead.id}
            clientNom={`${selectedLead.nom} ${selectedLead.prenom}`}
          />
        </FloatingPanel>
      )}

      {/* Overlay */}
      {showOverlay && selectedLead && (
        <div className="overlayAC" ref={overlayRef}>
          <div className="inoverlayLead" ref={inoverlayRef}>
            <button
              className="close-button"
              onClick={() => {
                setShowOverlay(false);
                setShowMenu(false);
              }}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "transparent",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                zIndex: 10000,
              }}
            >
              <button
                aria-label="Fermer"
                className="p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </button>

            {/* Vue Détails */}
            {!isEditing ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  padding: "0 5%",
                  boxSizing: "border-box",
                }}
              >
                <div className="flex  justify-between mb-6 items-center">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#9B1B5D] to-[#7A1548] flex items-center justify-center">
                      <Building2 className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <div className="text-1xl font-bold text-slate-900">
                        {selectedLead.prenom} {selectedLead.nom}{" "}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex items-center gap-2">
                          <div
                            className={`px-2 py-1 text-xs font-semibold rounded-md border bg-yellow-100 text-yellow-800 border-yellow-300`}
                          >
                            Prospect
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mr-10">
                    <div
                      className={`cursor-pointer flex items-center  h-8 px-3  rounded-md font-semibold text-sm bg-white text-black border border-slate-300 hover:bg-slate-50`}
                      onClick={() => setIsEditing(true)}
                    >
                      <p>Modifier</p>
                    </div>
                    <div className="relative">
                      <button
                        onClick={() => setShowMenu(!showMenu)}
                        className="p-2 rounded-md hover:bg-gray-100"
                      >
                        <MoreVertical className="w-5 h-5 text-gray-600" />
                      </button>

                      {showMenu && (
                        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                          <div
                            className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleAfficheLM(selectedLead);
                              setShowMenu(false);
                            }}
                          >
                            Créer une LM
                          </div>

                          <div
                            className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setShowRdvPanel(true);
                              setShowMenu(false);
                            }}
                          >
                            Créer une Note de RDV
                          </div>

                          <div
                            className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleDelete();
                              setShowMenu(false);
                            }}
                          >
                            Supprimer
                          </div>
                          <div
                            className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleArchive();
                              setShowMenu(false);
                            }}
                          >
                            Archiver
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-blue-700">
                        Missions en cours
                      </span>
                      <div className="bg-purple-600 text-white text-xs py-1 px-1.5 font-semibold rounded-md">
                        {selectedLead.creationSct?.filter(
                          (todo) =>
                            todo.status !== "Terminer" &&
                            todo.status !== "Terminé"
                        ).length || 0}
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-purple-700">
                        Tâches en cours
                      </span>

                      <div className="bg-[#8B1538] text-white text-xs py-1 px-1.5 font-semibold rounded-md">
                        {selectedLead.ToDo?.filter(
                          (todo) =>
                            todo.status === "En cours" &&
                            Array.isArray(todo.users) &&
                            todo.users.length > 0
                        ).length || 0}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid w-full grid-cols-2 bg-slate-100 p-1 h-auto rounded-md mb-6">
                  {[
                    { value: "general", label: "Général" },

                    { value: "timeline", label: "Timeline" },
                  ].map((tab) => (
                    <button
                      key={tab.value}
                      onClick={() => setActiveTab(tab.value)}
                      className={`py-2 text-xs rounded-md transition-colors font-medium
              ${
                activeTab === tab.value
                  ? "bg-white"
                  : "bg-transparent hover:bg-slate-200"
              }
            `}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div>
                  {activeTab === "general" && (
                    <>
                      <div className="bg-white rounded-lg p-4 border border-slate-200 mb-5">
                        <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-[#9B1B5D]" />
                          Information de Rendez-vous
                        </h3>

                        <div className="space-y-3 text-sm">
                          <div className="items-start justify-between">
                            <span className="text-slate-500">
                              Objet du Rendez-vous :{" "}
                            </span>
                            <span className="font-medium text-slate-900 text-right">
                              {selectedLead.objetRDV || "-"}
                            </span>
                          </div>

                          {selectedLead.user && (
                            <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
                              <div className="flex items-center gap-2 text-xs text-slate-600">
                                <Calendar className="w-4 h-4 text-[#9B1B5D]" />
                                <span>
                                  Le{" "}
                                  {new Date(
                                    selectedLead.dateRDV
                                  ).toLocaleDateString("fr-FR", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                  })}{" "}
                                  à {selectedLead.heureRDV}
                                </span>
                              </div>

                              <div className="flex items-center gap-2 text-xs text-slate-600">
                                <MapPin className="w-4 h-4 text-[#9B1B5D]" />
                                <span>{selectedLead.lieuRDV || "-"}</span>
                              </div>

                              <div className="flex items-center gap-2 text-xs text-slate-600">
                                <User className="w-4 h-4 text-[#9B1B5D]" />
                                <span>
                                  {selectedLead.user.name}{" "}
                                  {selectedLead.user.lastName}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Information client */}
                        <div className="bg-white rounded-lg p-4 border border-slate-200">
                          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-[#9B1B5D]" />
                            Information client
                          </h3>
                          <div className="space-y-2">
                            {/* Infos générales */}
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Civilité</span>
                              <span className="font-medium text-slate-900">
                                {selectedLead.civilite || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Nom</span>
                              <span className="font-medium text-slate-900">
                                {selectedLead.nom || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Prenom</span>
                              <span className="font-medium text-slate-900">
                                {selectedLead.prenom || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Adresse</span>
                              <span className="font-medium text-slate-900">
                                {selectedLead.adresse || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Telephone</span>
                              <span className="font-medium text-slate-900">
                                {selectedLead.tel || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Email</span>
                              <span className="font-medium text-slate-900">
                                {selectedLead.mail || "-"}
                              </span>
                            </div>

                            {/* Contact représentant */}
                            <div className="bg-slate-50 rounded-lg p-2 mt-2 space-y-1.5">
                              <p className="text-xs font-semibold text-slate-700 mb-1">
                                Structure existante
                              </p>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-500">
                                  Forme Social
                                </span>
                                <span className="font-medium text-slate-900">
                                  {selectedLead.formeSociale || "-"} &nbsp;
                                  {selectedLead.sociale}{" "}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-500">
                                  Dénomination Social
                                </span>
                                <span className="font-medium text-slate-900">
                                  {selectedLead.societeNom || "-"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Équipe Cabinet */}
                        <div className="bg-white rounded-lg p-4 border border-slate-200">
                          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <Users className="w-4 h-4 text-[#9B1B5D]" />
                            Équipe Cabinet
                          </h3>

                          <div className="space-y-2.5">
                            {/* Expert comptable */}
                            <div className="flex items-center gap-2 p-2 bg-[#9B1B5D]/5 rounded-lg">
                              <div className="w-8 h-8 rounded-full bg-[#9B1B5D] flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-slate-500">
                                  Collaborateur
                                </p>
                                <p className="text-xs font-semibold text-slate-900 truncate">
                                  {" "}
                                  {selectedLead.user.name}{" "}
                                  {selectedLead.user.lastName}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-5">
                        <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#9B1B5D]" />
                          Documents liés
                        </h3>

                        <LeadDocumentsList lead={selectedLead} />
                      </div>
                      <br />
                    </>
                  )}
                  {activeTab === "timeline" && (
                    <div className="bg-white border border-slate-200 rounded-lg p-4 mt-0">
                      {/* Header */}
                      <div className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-900 mb-4">
                        <div className="w-2 h-2 rounded-full bg-[#8B1538]"></div>
                        Timeline
                      </div>

                      {/* Historique dynamique */}
                      {allTimeline &&
                        allTimeline.length > 0 &&
                        allTimeline.map((item) => (
                          <div
                            key={item.id}
                            style={{
                              position: "relative",
                              paddingLeft: "20px",
                              marginBottom: "15px",
                              borderLeft: "2px solid #cbd5e1",
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                left: "-6px",
                                top: "6px",
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                                backgroundColor: "#2563eb",
                                border: "2px solid #fff",
                              }}
                            ></div>

                            <div
                              style={{
                                background: "#eff6ff",
                                borderRadius: "8px",
                                padding: "10px",
                                border: "1px solid #cbd5e1",
                              }}
                            >
                              <div className="flex justify-between items-start mb-1">
                                <p className="text-[13px] font-semibold text-blue-800">
                                  {item.titre}
                                </p>
                                <span className="text-xs text-blue-700">
                                  {new Date(item.createdAt).toLocaleDateString(
                                    "fr-FR",
                                    {
                                      day: "numeric",
                                      month: "long",
                                      year: "numeric",
                                    }
                                  )}
                                </span>
                              </div>

                              {item.type === "noteRDV" && (
                                <span
                                  onClick={() =>
                                    setOpenDetails((prev) => ({
                                      ...prev,
                                      [item.id]: !prev[item.id],
                                    }))
                                  }
                                  style={{
                                    color: "#1e40af",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    textDecoration: "underline",
                                  }}
                                >
                                  {openDetails[item.id]
                                    ? "masquer"
                                    : "consulter"}
                                </span>
                              )}

                              {item.titre === "Création de mandat" &&
                                item.description && (
                                  <a
                                    href={item.description}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                      color: "#2563eb",
                                      textDecoration: "underline",
                                      fontWeight: "500",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Voir le mandat PDF
                                  </a>
                                )}

                              {item.titre === "Mandat signé" && (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    padding: "2px 10px",
                                    borderRadius: "9999px",
                                    backgroundColor: "#dcfce7",
                                    color: "#166534",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                  }}
                                >
                                  ✓ Signé
                                  {item.description && (
                                    <a
                                      href={item.description}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{
                                        color: "#166534",
                                        textDecoration: "underline",
                                      }}
                                    >
                                      Voir le document
                                    </a>
                                  )}
                                </span>
                              )}

                              {item.titre === "Mandat annulé" && (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    padding: "2px 10px",
                                    borderRadius: "9999px",
                                    backgroundColor: "#fee2e2",
                                    color: "#991b1b",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                  }}
                                >
                                  ✗ Annulé
                                  {item.description
                                    ? ` — ${item.description}`
                                    : ""}
                                </span>
                              )}

                              {openDetails[item.id] &&
                                item.type === "noteRDV" && (
                                  <div
                                    style={{
                                      marginTop: "10px",
                                      background: "#ffffff",
                                      borderRadius: "8px",
                                      padding: "10px",
                                      border: "1px solid #cbd5e1",
                                    }}
                                  >
                                    <h4 className="text-sm font-semibold text-blue-800 mb-2">
                                      Note du rendez-vous
                                    </h4>

                                    <div className="text-[13px] text-blue-900 mb-2">
                                      <strong>Note complète :</strong>
                                      <p className="mt-1 whitespace-pre-line">
                                        {item.noteDeBase}
                                      </p>
                                    </div>

                                    <div className="text-[13px] text-blue-900">
                                      <strong>Résumé :</strong>
                                      <p className="mt-1 whitespace-pre-line">
                                        {item.noteResume}
                                      </p>
                                    </div>

                                    {item.documentUrl && (
                                      <a
                                        href={item.documentUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-[#840040] hover:underline"
                                      >
                                        <FileTextIcon size={14} />
                                        Consulter le document
                                        {item.documentName
                                          ? ` — ${item.documentName}`
                                          : ""}
                                      </a>
                                    )}
                                  </div>
                                )}

                              {item.titre ===
                                "Formulaire de Rendez-vous renseigné" && (
                                <span
                                  onClick={() =>
                                    setOpenDetails((prev) => ({
                                      ...prev,
                                      [item.id]: !prev[item.id],
                                    }))
                                  }
                                  style={{
                                    color: "#1e40af",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    textDecoration: "underline",
                                  }}
                                >
                                  {openDetails[item.id]
                                    ? "masquer détails"
                                    : "voir détails"}
                                </span>
                              )}

                              {openDetails[item.id] &&
                                item.titre ===
                                  "Formulaire de Rendez-vous renseigné" && (
                                  <div
                                    style={{
                                      marginTop: "10px",
                                      background: "#ffffff",
                                      borderRadius: "8px",
                                      padding: "10px",
                                      border: "1px solid #cbd5e1",
                                    }}
                                  >
                                    <h4 className="text-sm font-semibold text-blue-800 mb-3">
                                      Détails Formulaire rempli
                                    </h4>

                                    <table
                                      style={{
                                        width: "100%",
                                        borderCollapse: "collapse",
                                      }}
                                    >
                                      <tbody>
                                        {[
                                          [
                                            "Nom complet",
                                            selectedLead.prospect_nomComplet,
                                          ],
                                          [
                                            "Email",
                                            selectedLead.prospect_email,
                                          ],
                                          [
                                            "Téléphone",
                                            selectedLead.prospect_telephone,
                                          ],
                                          [
                                            "Adresse",
                                            selectedLead.prospect_adresse,
                                          ],
                                          [
                                            "Type de prospect",
                                            selectedLead.prospect_prospectType,
                                          ],
                                          [
                                            "Description du projet",
                                            selectedLead.prospect_descriptionProjet,
                                          ],
                                          [
                                            "Nombre d’associés",
                                            selectedLead.prospect_nombreAssocies,
                                          ],
                                          [
                                            "Forme sociale",
                                            selectedLead.prospect_formSociale,
                                          ],
                                          [
                                            "Numéro SIRET",
                                            selectedLead.prospect_numSiret,
                                          ],
                                          [
                                            "Nom de la société",
                                            selectedLead.prospect_nomSociete,
                                          ],
                                          [
                                            "Adresse société",
                                            selectedLead.prospect_adresseSociete,
                                          ],
                                          [
                                            "Date de création société",
                                            selectedLead.prospect_dateCreationSociete,
                                          ],
                                          [
                                            "Description du besoin",
                                            selectedLead.prospect_descriptionBesoin,
                                          ],
                                          [
                                            "Service sélectionné",
                                            selectedLead.prospect_selectedService,
                                          ],
                                        ].map(([label, value]) => (
                                          <tr key={label}>
                                            <td
                                              style={{
                                                fontWeight: "600",
                                                padding: "5px 10px",
                                                width: "35%",
                                                color: "#1e3a8a",
                                              }}
                                            >
                                              {label} :
                                            </td>
                                            <td
                                              style={{
                                                padding: "5px 10px",
                                                color: "#1e3a8a",
                                              }}
                                            >
                                              {value ?? " "}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                            </div>
                          </div>
                        ))}

                      {/* Création LEAD */}
                      <div
                        style={{
                          position: "relative",
                          paddingLeft: "20px",
                          marginBottom: "15px",
                          borderLeft: "2px solid #cbd5e1",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            left: "-6px",
                            top: "6px",
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            backgroundColor: "#2563eb",
                            border: "2px solid #fff",
                          }}
                        ></div>

                        <div
                          style={{
                            background: "#eff6ff",
                            borderRadius: "8px",
                            padding: "10px",
                            border: "1px solid #cbd5e1",
                          }}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <p className="text-[13px] font-semibold text-blue-800">
                              Création de la fiche LEAD
                            </p>
                            <span className="text-xs text-blue-700">
                              {new Date(
                                selectedLead.createdAt
                              ).toLocaleDateString("fr-FR", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })}
                            </span>
                          </div>

                          <p className="text-[13px] text-blue-900">
                            Création de la fiche LEAD avec{" "}
                            {selectedLead.specificite}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Vue Édition */
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <div
                  style={{ width: "100%" }}
                  className="px-8 pt-6 pb-4 border-b  mb-5 flex items-center justify-between"
                >
                  <div className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#9B1B5D] flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-white" />
                    </div>
                    Modifier le Prospect
                  </div>
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSave();
                  }}
                  className="w-full px-10"
                >
                  <div className="bg-gradient-to-br from-[#9B1B5D]/5 via-white to-transparent rounded-xl p-5 border-2 border-[#9B1B5D]/20">
                    {/* Titre */}
                    <h4 className="text-sm font-bold text-[#9B1B5D] mb-4 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Informations essentielles
                    </h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4 mt-3">
                        <div>
                          <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                            Civilité
                          </label>
                          <select
                            name="civilite"
                            onChange={handleChange}
                            value={formValues.civilite}
                            required
                            className={`
                    mt-2
                    h-11
                    w-full
                    rounded-md
                    bg-white
                    px-3
                    text-sm
                    border-2
                    outline-none
                    transition-all
                    ${
                      formValues.civilite
                        ? "border-emerald-300 focus:border-emerald-400"
                        : "border-slate-300 focus:border-slate-400"
                    }
                  `}
                          >
                            <option value="">--Civilité--</option>
                            <option value="M.">Monsieur</option>
                            <option value="Mme.">Madame</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                            Nom
                          </label>
                          <input
                            name="nom"
                            style={{ width: "100%" }}
                            value={formValues.nom}
                            placeholder="Nom"
                            onChange={handleChange}
                            required
                            className={`
                    mt-2
                    h-11
                    w-full
                    rounded-md
                    bg-white
                    px-3
                    text-sm
                    border-2
                    outline-none
                    transition-all
                    ${
                      formValues.nom
                        ? "border-emerald-300 focus:border-emerald-400"
                        : "border-slate-300 focus:border-slate-400"
                    }
                  `}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                            Prenom
                          </label>
                          <input
                            value={formValues.prenom}
                            name="prenom"
                            style={{ width: "100%" }}
                            placeholder="Prénom"
                            onChange={handleChange}
                            className={`
                    mt-2
                    h-11
                    w-full
                    rounded-md
                    bg-white
                    px-3
                    text-sm
                    border-2
                    outline-none
                    transition-all
                    ${
                      formValues.prenom
                        ? "border-emerald-300 focus:border-emerald-400"
                        : "border-slate-300 focus:border-slate-400"
                    }
                  `}
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div>
                          <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                            Téléphone
                          </label>
                          <input
                            name="tel"
                            style={{ width: "100%" }}
                            value={formValues.tel}
                            placeholder="Téléphone"
                            onChange={handleChange}
                            required
                            className={`
                    mt-2
                    h-11
                    w-full
                    rounded-md
                    bg-white
                    px-3
                    text-sm
                    border-2
                    outline-none
                    transition-all
                    ${
                      formValues.tel
                        ? "border-emerald-300 focus:border-emerald-400"
                        : "border-slate-300 focus:border-slate-400"
                    }
                  `}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                            Email
                          </label>
                          <input
                            value={formValues.mail}
                            name="mail"
                            style={{ width: "100%" }}
                            placeholder="Email"
                            onChange={handleChange}
                            className={`
                    mt-2
                    h-11
                    w-full
                    rounded-md
                    bg-white
                    px-3
                    text-sm
                    border-2
                    outline-none
                    transition-all
                    ${
                      formValues.mail
                        ? "border-emerald-300 focus:border-emerald-400"
                        : "border-slate-300 focus:border-slate-400"
                    }
                  `}
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                          Adresse complète
                        </label>
                        <input
                          value={formValues.adresse}
                          name="adresse"
                          style={{ width: "100%" }}
                          placeholder="Adresse"
                          onChange={handleChange}
                          className={`
                    mt-2
                    h-11
                    w-full
                    rounded-md
                    bg-white
                    px-3
                    text-sm
                    border-2
                    outline-none
                    transition-all
                    ${
                      formValues.adresse
                        ? "border-emerald-300 focus:border-emerald-400"
                        : "border-slate-300 focus:border-slate-400"
                    }
                  `}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <label className="text-sm font-semibold text-slate-700 flex items-center gap-1 mt-5">
                    Entreprise (si existant)
                  </label>

                  <div className="grid grid-cols-3 gap-4 mt-3">
                    <div>
                      <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                        Forme Sociale
                      </label>
                      <select
                        name="formeSociale"
                        onChange={handleChange}
                        value={formValues.formeSociale}
                        required
                        className={`
                    mt-2
                    h-11
                    w-full
                    rounded-md
                    bg-white
                    px-3
                    text-sm
                    border-2
                    outline-none
                    transition-all
                    ${
                      formValues.formeSociale
                        ? "border-emerald-300 focus:border-emerald-400"
                        : "border-slate-300 focus:border-slate-400"
                    }
                  `}
                      >
                        <option value="">Forme Sociale</option>
                        {[
                          "E.I",
                          "E.I.R.L",
                          "EARL",
                          "EURL",
                          "SARL",
                          "SASU",
                          "SAS",
                          "SCI",
                          "SCCV",
                          "LMNP",
                          "GFA",
                          "SC",
                          "SCP",
                          "SEL",
                          "N/A",
                        ].map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                        Dénomination Sociale
                      </label>
                      <input
                        name="societeNom"
                        style={{ width: "100%" }}
                        value={formValues.societeNom}
                        placeholder="Dénomination Sociale"
                        onChange={handleChange}
                        required
                        className={`
                    mt-2
                    h-11
                    w-full
                    rounded-md
                    bg-white
                    px-3
                    text-sm
                    border-2
                    outline-none
                    transition-all
                    ${
                      formValues.societeNom
                        ? "border-emerald-300 focus:border-emerald-400"
                        : "border-slate-300 focus:border-slate-400"
                    }
                  `}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                        SIREN
                      </label>
                      <input
                        value={formValues.sirenSociete}
                        name="sirenSociete"
                        style={{ width: "100%" }}
                        placeholder="SIREN"
                        onChange={handleChange}
                        className={`
                    mt-2
                    h-11
                    w-full
                    rounded-md
                    bg-white
                    px-3
                    text-sm
                    border-2
                    outline-none
                    transition-all
                    ${
                      formValues.sirenSociete
                        ? "border-emerald-300 focus:border-emerald-400"
                        : "border-slate-300 focus:border-slate-400"
                    }
                  `}
                        required
                      />
                    </div>
                  </div>
                  <br />

                  <div className="mt-0 bg-slate-50 rounded-xl border border-slate-200">
                    {/* Trigger */}
                    <div className="w-full flex items-center justify-between p-3 hover:bg-slate-100 transition-colors rounded-t-xl">
                      {/* Titre */}
                      <span className="text-sm font-semibold flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-500" />
                        Prospect souhaite un RDV
                      </span>
                    </div>
                    <div className="px-8 space-y-3">
                      <div className="grid grid-cols-2 gap-[5%]">
                        <div>
                          <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                            Date de rendez-vous
                          </label>
                          <input
                            name="dateRDV"
                            type="date"
                            value={formValues.dateRDV?.split("T")[0] || ""}
                            onChange={handleChange}
                            className="w-full rounded-md border-2 border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:border-emerald-400 transition-all"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                            Heure de rendez-vous
                          </label>
                          <input
                            name="heureRDV"
                            type="time"
                            value={formValues.heureRDV || ""}
                            onChange={handleChange}
                            className="w-full rounded-md border-2 border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:border-emerald-400 transition-all"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                          Site
                        </label>
                        <select
                          name="lieuRDV"
                          value={formValues.lieuRDV || ""}
                          onChange={handleChange}
                          className="w-full rounded-md border-2 border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:border-emerald-400 transition-all"
                        >
                          <option value="">— Site —</option>
                          <option value="27 rue Vallon Hoarau, LE TAMPON (97430)">27 rue Vallon Hoarau, LE TAMPON (97430)</option>
                          <option value="19 chemin du Château d'Eau, Espace Turquoise n° 23, SAINT-PIERRE (97410)">19 chemin du Château d'Eau, Espace Turquoise n° 23, SAINT-PIERRE (97410)</option>
                          <option value="RDV en ligne">RDV en ligne</option>
                          <option value="Rendez-vous téléphonique">Rendez-vous téléphonique</option>
                        </select>
                      </div>
                      {formValues.lieuRDV === "RDV en ligne" && (
                        <div>
                          <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                            Lien Google Meet
                          </label>
                          <input
                            name="lienRDV"
                            type="url"
                            value={formValues.lienRDV || ""}
                            onChange={handleChange}
                            placeholder="https://meet.google.com/..."
                            className="w-full rounded-md border-2 border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:border-emerald-400 transition-all"
                          />
                        </div>
                      )}
                      <div>
                        <textarea
                          name="objetRDV"
                          rows={2}
                          value={formValues.objetRDV || ""}
                          onChange={handleChange}
                          placeholder="Objet du Rendez-vous..."
                          className="w-full rounded-md border-2 border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:border-emerald-400 transition-all"
                        />
                      </div>
                      <br />
                    </div>
                  </div>

                  <br />
                  {/* Champs fichiers */}
                  {[
                    { key: "pieceIdentite", label: "Pièce d'identité" },
                    { key: "bilanDocument", label: "Bilan" },
                    {
                      key: "identityDocument",
                      label: "Justificatif d'identité",
                    },
                    { key: "kbisDocument", label: "KBIS" },
                    { key: "statusDocument", label: "Statuts" },
                  ].map((doc) => (
                    <div key={doc.key}>
                      <span>{doc.label} :</span>
                      <br />
                      <input
                        type="file"
                        name={doc.key}
                        onChange={handleFileChange}
                      />
                      <br />
                      <br />
                    </div>
                  ))}

                  <div className="sticky bottom-0 bg-white border-t mt-4 px-8 py-4 flex items-center justify-between">
                    <button
                      type="button"
                      variant="outline"
                      onClick={() => setIsEditing(false)}
                      className={`
                                px-8
                                h-11
                                border-2
                                border-slate-300
                                text-slate-700
                                rounded-md
                                font-medium
                                hover:bg-slate-100
                                transition-colors
                              `}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className={`
                                w-1/2
                                h-11
                                bg-[#9B1B5D]
                                hover:bg-[#7A1548]
                                text-white
                                text-base
                                font-medium
                                rounded-md
                                shadow-lg shadow-[#9B1B5D]/20
                                transition-colors
                                flex items-center justify-center
                                gap-2
                              `}
                    >
                      <Check className="w-5 h-5 mr-2" />
                      Modifier le Prospect
                    </button>
                  </div>
                </form>
                <br />
                <br />
                <br />
                <br />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default RdvLM;
